/**
 * One client's plan in the master list: who is running it, which plan it is, and how far through.
 *
 * ── The CLIENT is the title and the PLAN is the sub-line, which is the whole point of the scope ─
 * The templates list is titled by the plan because that is what a trainer is choosing between there.
 * Here they are choosing between people, the list is alphabetical by surname, and the plan's name is
 * what qualifies the person. Swapping the two would make the scope a second copy of the first.
 *
 * ── The bar is the app's ONE plan progress bar ────────────────────────────────────────────────
 * `shared/ui/progressBar`, the same 6px track and accent fill `PlanCard` draws (owner's polish of
 * 4 November 2026: "one golden standard for the whole app"). It replaced a 2.5px bar in the plan's
 * category ink. Its colour never turns with the number: a bar that went red or green would be a
 * judgement on the client, and this screen does not make one.
 *
 * ── The mode is a symbol, and the workout count is the hero's ────────────────────────────────
 * The row used to carry `3 / 4 treningi` beside the mode mark; the owner's polish took the fraction
 * off the row (the hero prints `3 z 4 treningów` under the percent) and left the mark alone. A
 * PAUSED plan still says `Wstrzymany` in that cell, before the mark: owner decision D7 made the word
 * stand where the count stood, and with the count gone a paused row would otherwise look exactly
 * like a live one. The percent and the bar are unchanged — nothing is moving, not nothing happened.
 *
 * The row's interaction is `TemplateRow`'s, value for value — `mousemove` rather than `mouseenter`,
 * focus into the row selects it, the whole row is a `Link`, and the ring is drawn on the row rather
 * than on the stretched anchor. That header carries the reasoning; this one does not restate it.
 */

import { Link } from '@tanstack/react-router';

import { AvatarChip } from '~/shared/ui/AvatarChip';
import photoBox from '~/shared/ui/photoBox.module.css';
import progressBar from '~/shared/ui/progressBar.module.css';
import type { PhotoReveal } from '~/shared/lib/usePhotoReveal';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import type { ClientPlanRow as ClientPlanRowModel } from '../model/clientPlanRow';
import { PlanModeMark } from './PlanModeMark';
import listRow from './listRow.module.css';
import styles from './ClientPlanRow.module.css';

interface ClientPlanRowProps {
  /** The list's key for this row, which is also its arrow-navigation handle. See `ListRows`. */
  readonly rowKey: string;
  readonly row: ClientPlanRowModel;
  /**
   * The client's name as the row draws it — `model/personName`'s answer, so a client the backend
   * composed no name for still gets a link with an accessible name rather than an empty one.
   */
  readonly name: string;
  /** `Kategoria: Siła`, or `null` where the plan carries none. Composed by `ListRows`. */
  readonly categorySpoken: string | null;
  /** `Wstrzymany` for a paused plan, `null` for a live one. Composed by `ListRows`. */
  readonly pausedLabel: string | null;
  /** The percent as the trainer reads it, formatted by the caller. */
  readonly percentLabel: string;
  /** The bar's whole fact in one sentence, because a width is not readable and `40%` has no subject. */
  readonly progressSpoken: string;
  readonly selected: boolean;
  readonly onPoint: () => void;
  /**
   * The list's shared photograph group — every cover in it appears on one frame rather than as its
   * own file lands. Owned by `ListRows`, because the group is the list; see `usePhotoReveal`.
   */
  readonly photo: PhotoReveal;
}

export function ClientPlanRow({
  rowKey,
  row,
  name,
  categorySpoken,
  pausedLabel,
  percentLabel,
  progressSpoken,
  selected,
  onPoint,
  photo,
}: ClientPlanRowProps) {
  return (
    <li
      className={`${String(listRow.row)} ${String(styles.row)} ${
        selected ? String(listRow.rowSelected) : ''
      }`}
      // `undefined` on the row the pane is ALREADY describing: a pointer resting on a row fires
      // `mousemove` sixty to a hundred and twenty times a second, and every one of those was a
      // round of no-op work to select what was already selected.
      //
      // Still `mousemove` and NOT `mouseenter`. A dialog makes the rest of the page `inert`, so no
      // row is hoverable while one is open, and the instant it closes the browser re-evaluates
      // hover and fires `mouseenter` under a pointer that never moved — on the roster that swapped
      // the panel to a client nobody chose and dropped focus to `<body>`.
      onMouseMove={selected ? undefined : onPoint}
      onFocusCapture={onPoint}
    >
      {selected && <span className={listRow.selectionBar} aria-hidden />}

      {/* The name beside it carries the person, so the disc says nothing of its own — that is
        * `AvatarChip`'s standing rule and the reason both of its branches are decorative. */}
      <span className={styles.avatarCell}>
        <AvatarChip
          initials={row.client.initials}
          avatarUrl={row.client.avatarUrl}
          variant="library"
          loading="lazy"
        />
      </span>

      <span className={listRow.cover}>
        <img
          className={`${String(photoBox.photo)} ${String(listRow.coverImage)} ${photo.holding} ${photo.reveal}`}
          src={row.thumbUrl}
          alt=""
          loading="lazy"
          decoding="async"
          // A failure is an arrival — see the twin in `TemplateRow`.
          onLoad={photo.onPhotoSettled}
          onError={photo.onPhotoSettled}
        />
      </span>

      <span className={listRow.identity}>
        <span className={listRow.nameLine}>
          <Link
            className={listRow.rowLink}
            to="/plans/$planId"
            // The client's OWN fork. Opening the template it came from would show the trainer a
            // different document under this client's name.
            params={{ planId: String(row.planTemplateId) }}
            data-plan-row={rowKey}
            title={name}
            // The plan's category is not drawn on a row at all (D10, withdrawn), so a reader
            // user meets a facet in the filter panel they cannot hear on a row. `TemplateRow`
            // carries the reasoning for the `aria-label`.
            aria-label={categorySpoken === null ? undefined : `${name}, ${categorySpoken}`}
          >
            {name}
          </Link>
        </span>
        <span className={listRow.sub}>{row.planName}</span>
        {/* Decorative: the width is the percent printed beside it, and the sentence under the
          * figure carries the fact. */}
        <span className={`${String(progressBar.track)} ${String(styles.bar)}`} aria-hidden>
          <span className={progressBar.fill} style={{ inlineSize: `${String(row.progressPct)}%` }} />
        </span>
      </span>

      <span className={listRow.modeCell}>
        {pausedLabel === null ? null : <span className={listRow.modeLabel}>{pausedLabel}</span>}
        <PlanModeMark marker={row.schedulingMarker} className={listRow.mode} />
      </span>

      {/* `40%` on its own is a number with no subject, and the label to its left is a different
        * fact. One sentence is read in place of both marks. */}
      <span className={styles.progress}>
        <span className={styles.percent} aria-hidden>
          {percentLabel}
        </span>
        <span className={hidden.srOnly}>{progressSpoken}</span>
      </span>
    </li>
  );
}
